import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const VersionHistory = ({ onRestoreVersion }) => {
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [compareMode, setCompareMode] = useState(false);

  const versions = [
    {
      id: 1,
      version: 'v1.4',
      timestamp: '2025-12-06 11:45:23',
      author: 'AI Assistant',
      changes: 'Updated executive summary with latest incident data',
      content: 'Q4 2025 Safety Performance: Overall incident rate decreased by 18% compared to Q3...',
      wordCount: 847,
      type: 'ai-generated',
    },
    {
      id: 2,
      version: 'v1.3',
      timestamp: '2025-12-06 10:30:15',
      author: 'John Smith',
      changes: 'Manual edits to compliance section',
      content: 'The facility maintains full compliance with OSHA 1910 standards...',
      wordCount: 823,
      type: 'manual-edit',
    },
    {
      id: 3,
      version: 'v1.2',
      timestamp: '2025-12-06 09:15:42',
      author: 'AI Assistant',
      changes: 'Added trend analysis and recommendations',
      content: 'Analysis of near-miss reports over the past 6 months reveals...',
      wordCount: 756,
      type: 'ai-generated',
    },
    {
      id: 4,
      version: 'v1.1',
      timestamp: '2025-12-05 16:20:30',
      author: 'Sarah Johnson',
      changes: 'Initial draft review and formatting',
      content: 'Initial safety report draft covering Q4 performance metrics...',
      wordCount: 692,
      type: 'manual-edit',
    },
    {
      id: 5,
      version: 'v1.0',
      timestamp: '2025-12-05 15:00:00',
      author: 'AI Assistant',
      changes: 'Initial AI-generated report',
      content: 'Safety performance summary for Q4 2025 fiscal quarter...',
      wordCount: 645,
      type: 'ai-generated',
    },
  ];

  const handleRestore = (version) => {
    if (window.confirm(`Restore version ${version?.version}? Current content will be replaced.`)) {
      onRestoreVersion(version?.content);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Version History</h3>
          <p className="text-sm text-muted-foreground">
            Track changes and restore previous versions
          </p>
        </div>
        <button
          onClick={() => setCompareMode(!compareMode)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors text-sm"
        >
          <Icon name={compareMode ? 'X' : 'GitCompare'} size={18} />
          {compareMode ? 'Exit Compare' : 'Compare Versions'}
        </button>
      </div>

      <div className="space-y-4">
        {versions?.map((version) => (
          <div
            key={version?.id}
            className={`bg-card border rounded-lg p-6 transition-all ${
              selectedVersion?.id === version?.id ? 'border-primary shadow-md' : 'border-border'
            }`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start gap-4 flex-1">
                <div
                  className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    version?.type === 'ai-generated' ?'bg-primary/10 text-primary' :'bg-blue-500/10 text-blue-500'
                  }`}
                >
                  <Icon name={version?.type === 'ai-generated' ? 'Sparkles' : 'User'} size={24} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-lg font-semibold text-foreground">{version?.version}</h4>
                    <span
                      className={`px-2 py-1 rounded-lg text-xs font-medium ${
                        version?.type === 'ai-generated' ?'bg-primary/10 text-primary' :'bg-blue-500/10 text-blue-500'
                      }`}
                    >
                      {version?.type === 'ai-generated' ? 'AI Generated' : 'Manual Edit'}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-1">{version?.changes}</p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Icon name="Clock" size={12} />
                      {version?.timestamp}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="User" size={12} />
                      {version?.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="FileText" size={12} />
                      {version?.wordCount} words
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {compareMode && (
                  <button
                    onClick={() => setSelectedVersion(version)}
                    className={`p-2 rounded-lg transition-colors ${
                      selectedVersion?.id === version?.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground hover:bg-muted/80'
                    }`}
                  >
                    <Icon name={selectedVersion?.id === version?.id ? 'CheckCircle' : 'Circle'} size={18} />
                  </button>
                )}
                <button
                  onClick={() => handleRestore(version)}
                  className="p-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
                  title="Restore this version"
                >
                  <Icon name="RotateCcw" size={18} />
                </button>
                <button className="p-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors">
                  <Icon name="MoreVertical" size={18} />
                </button>
              </div>
            </div>

            <div className="bg-muted/30 rounded-lg p-4 border border-border">
              <p className="text-sm text-foreground line-clamp-2">{version?.content}</p>
              <button className="text-xs text-primary hover:text-primary/80 mt-2">
                View Full Content
              </button>
            </div>
          </div>
        ))}
      </div>

      {versions?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Clock" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-lg font-medium text-foreground mb-2">No version history</p>
          <p className="text-sm text-muted-foreground">Versions will appear here as you generate and edit content</p>
        </div>
      )}
    </div>
  );
};

export default VersionHistory;